import React, { Suspense, useEffect } from "react";
import { BrowserRouter, Routes, Route, Outlet } from "react-router-dom";
import { GlobalContextProviders } from "./components/_globalContextProviders";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { Spinner } from "./components/Spinner";
import { PromoCampaignBanner } from "./components/PromoCampaignBanner";

// Layouts (loaded immediately - small files)
import PageLayout_login from "./pages/login.pageLayout.tsx";
import PageLayout_inscription from "./pages/inscription.pageLayout.tsx";
import PageLayout_verifyEmail from "./pages/verify-email.pageLayout.tsx";
import PageLayout_motDePasseOublie from "./pages/mot-de-passe-oublie.pageLayout.tsx";
import PageLayout_reinitialiserMotDePasse from "./pages/reinitialiser-mot-de-passe.pageLayout.tsx";
import PageLayout_tests from "./pages/tests.pageLayout.tsx";
import PageLayout_home from "./pages/home.pageLayout.tsx";
import PageLayout_compte from "./pages/compte.pageLayout.tsx";
import PageLayout_paiementConfirme from "./pages/paiement-confirme.pageLayout.tsx";
import PageLayout_tarifs from "./pages/tarifs.pageLayout.tsx";
import PageLayout_contact from "./pages/contact.pageLayout.tsx";
import PageLayout_exports from "./pages/exports.pageLayout.tsx";
import PageLayout_grille_matricielle_keno from "./pages/grille-matricielle-keno.pageLayout.tsx";
import PageLayout_reducteur from "./pages/reducteur.pageLayout.tsx";
import PageLayout_structures from "./pages/structures.pageLayout.tsx";
import PageLayout_relations from "./pages/relations.pageLayout.tsx";
import PageLayout_comparateur from "./pages/comparateur.pageLayout.tsx";
import PageLayout_admin_login from "./pages/admin.login.pageLayout.tsx";
import PageLayout_admin_activities from "./pages/admin.activities.pageLayout.tsx";
import PageLayout_espace_admin from "./pages/espace-admin.pageLayout.tsx";
import PageLayout_espace_admin_conversion from "./pages/espace-admin.conversion.pageLayout.tsx";
import PageLayout_espace_admin_cohortes from "./pages/espace-admin.cohortes.pageLayout.tsx";
import PageLayout_labo from "./pages/labo.pageLayout.tsx";
import PageLayout_quartets from "./pages/quartets.pageLayout.tsx";
import PageLayout_mes_grilles from "./pages/mes-grilles.pageLayout.tsx";
import PageLayout_mon_keno from "./pages/mon-keno.pageLayout.tsx";
import PageLayout_analyses_tirage_date from "./pages/analyses.tirage.$date.pageLayout.tsx";
import PageLayout_tirage_du_jour from "./pages/tirage-du-jour.pageLayout.tsx";
import PageLayout_prochain_tirage from "./pages/prochain-tirage.pageLayout.tsx";
import PageLayout_pronostics_keno from "./pages/pronostics-keno.pageLayout.tsx";
import PageLayout_notifications from "./pages/notifications.pageLayout.tsx";
import PageLayout_multiplicateurs from "./pages/multiplicateurs.pageLayout.tsx";
import PageLayout_numeros_communs from "./pages/numeros-communs.pageLayout.tsx";
import PageLayout_calendrier from "./pages/calendrier.pageLayout.tsx";
import PageLayout_mon_avis from "./pages/mon-avis.pageLayout.tsx";
import PageLayout_analyse_combinaison_keno from "./pages/analyse-combinaison-keno.pageLayout.tsx";
import PageLayout_taux_sortie_numeros_keno from "./pages/taux-sortie-numeros-keno.pageLayout.tsx";
import PageLayout_numeros_signature_keno from "./pages/numeros-signature-keno.pageLayout.tsx";
import PageLayout_numeros_plus_sortis_keno from "./pages/numeros-plus-sortis-keno.pageLayout.tsx";

// Homepage - static import (no flash on initial load)
import Page_home from "./pages/home.tsx";

// Pages (lazy loaded - split into separate chunks)
const Page_login = React.lazy(() => import("./pages/login.tsx"));
const Page_inscription = React.lazy(() => import("./pages/inscription.tsx"));
const Page_verifyEmail = React.lazy(() => import("./pages/verify-email.tsx"));
const Page_motDePasseOublie = React.lazy(() => import("./pages/mot-de-passe-oublie.tsx"));
const Page_reinitialiserMotDePasse = React.lazy(() => import("./pages/reinitialiser-mot-de-passe.tsx"));
const Page_tests = React.lazy(() => import("./pages/tests.tsx"));
const Page_compte = React.lazy(() => import("./pages/compte.tsx"));
const Page_compte_notifications = React.lazy(() => import("./pages/compte.notifications.tsx"));
const Page_paiementConfirme = React.lazy(() => import("./pages/paiement-confirme.tsx"));
const Page_tarifs = React.lazy(() => import("./pages/tarifs.tsx"));
const Page_contact = React.lazy(() => import("./pages/contact.tsx"));
const Page_exports = React.lazy(() => import("./pages/exports.tsx"));
const Page_grille_matricielle_keno = React.lazy(() => import("./pages/grille-matricielle-keno.tsx"));
const Page_reducteur = React.lazy(() => import("./pages/reducteur.tsx"));
const Page_structures = React.lazy(() => import("./pages/structures.tsx"));
const Page_relations = React.lazy(() => import("./pages/relations.tsx"));
const Page_comparateur = React.lazy(() => import("./pages/comparateur.tsx"));
const Page_admin_login = React.lazy(() => import("./pages/admin.login.tsx"));
const Page_admin_activities = React.lazy(() => import("./pages/admin.activities.tsx"));

// Espace Admin pages
const Page_espace_admin = React.lazy(() => import("./pages/espace-admin.tsx"));
const Page_espace_admin_users = React.lazy(() => import("./pages/espace-admin.users.tsx"));
const Page_espace_admin_users_new = React.lazy(() => import("./pages/espace-admin.users.new.tsx"));
const Page_espace_admin_users_id = React.lazy(() => import("./pages/espace-admin.users.$id.tsx"));
const Page_espace_admin_users_view = React.lazy(() => import("./pages/espace-admin.users.view.$id.tsx"));
const Page_espace_admin_tirages = React.lazy(() => import("./pages/espace-admin.tirages.tsx"));
const Page_espace_admin_tirages_new = React.lazy(() => import("./pages/espace-admin.tirages.new.tsx"));
const Page_espace_admin_tirages_id = React.lazy(() => import("./pages/espace-admin.tirages.$id.tsx"));
const Page_espace_admin_login_attempts = React.lazy(() => import("./pages/espace-admin.login-attempts.tsx"));
const Page_espace_admin_licenses = React.lazy(() => import("./pages/espace-admin.licenses.tsx"));
const Page_espace_admin_licenses_id = React.lazy(() => import("./pages/espace-admin.licenses.$id.tsx"));
const Page_espace_admin_notification_logs = React.lazy(() => import("./pages/espace-admin.notification-logs.tsx"));
const Page_espace_admin_sondages = React.lazy(() => import("./pages/espace-admin.sondages.tsx"));
const Page_espace_admin_announcements = React.lazy(() => import("./pages/espace-admin.announcements.tsx"));
const Page_espace_admin_messages = React.lazy(() => import("./pages/espace-admin.messages.tsx"));
const Page_espace_admin_logs = React.lazy(() => import("./pages/espace-admin.logs.tsx"));
const Page_espace_admin_email_preview = React.lazy(() => import("./pages/espace-admin.email-preview.tsx"));
const Page_espace_admin_conversion = React.lazy(() => import("./pages/espace-admin.conversion.tsx"));
const Page_espace_admin_cohortes = React.lazy(() => import("./pages/espace-admin.cohortes.tsx"));
const Page_espace_admin_promo_campaigns = React.lazy(() => import("./pages/espace-admin.promo-campaigns.tsx"));
const Page_notifications = React.lazy(() => import("./pages/notifications.tsx"));
const Page_labo = React.lazy(() => import("./pages/labo.tsx"));
const Page_quartets = React.lazy(() => import("./pages/quartets.tsx"));
const Page_mes_grilles = React.lazy(() => import("./pages/mes-grilles.tsx"));
const Page_mon_keno = React.lazy(() => import("./pages/mon-keno.tsx"));
const Page_multiplicateurs = React.lazy(() => import("./pages/multiplicateurs.tsx"));
const Page_numeros_communs = React.lazy(() => import("./pages/numeros-communs.tsx"));
const Page_calendrier = React.lazy(() => import("./pages/calendrier.tsx"));
const Page_analyses_tirage_date = React.lazy(() => import("./pages/analyses.tirage.$date.tsx"));
const Page_tirage_du_jour = React.lazy(() => import("./pages/tirage-du-jour.tsx"));
const Page_prochain_tirage = React.lazy(() => import("./pages/prochain-tirage.tsx"));
const Page_pronostics_keno = React.lazy(() => import("./pages/pronostics-keno.tsx"));
const Page_mon_avis = React.lazy(() => import("./pages/mon-avis.tsx"));
const Page_analyse_combinaison_keno = React.lazy(() => import("./pages/analyse-combinaison-keno.tsx"));
const Page_taux_sortie_numeros_keno = React.lazy(() => import("./pages/taux-sortie-numeros-keno.tsx"));
const Page_numeros_signature_keno = React.lazy(() => import("./pages/numeros-signature-keno.tsx"));
const Page_numeros_plus_sortis_keno = React.lazy(() => import("./pages/numeros-plus-sortis-keno.tsx"));

if (!window.requestIdleCallback) {
  window.requestIdleCallback = (cb: IdleRequestCallback, _options?: IdleRequestOptions): number => {
    return window.setTimeout(() => cb({ didTimeout: false, timeRemaining: () => 50 }), 1);
  };
}

import "./base.css";
import spaRoutes from "./routes.json";

// Routes de l'application React : source unique dans routes.json, lue aussi par
// le serveur (App\Service\SpaRouteCatalog) pour répondre 404 aux adresses
// inconnues. Toute nouvelle page s'ajoute dans routes.json.
const fileNameToRoute = new Map<string, string>(Object.entries(spaRoutes));

const fileNameToComponent = new Map<string, React.LazyExoticComponent<React.ComponentType<any>> | React.ComponentType<any>>([
  ["./pages/login.tsx", Page_login],
  ["./pages/inscription.tsx", Page_inscription],
  ["./pages/verify-email.tsx", Page_verifyEmail],
  ["./pages/mot-de-passe-oublie.tsx", Page_motDePasseOublie],
  ["./pages/reinitialiser-mot-de-passe.tsx", Page_reinitialiserMotDePasse],
  ["./pages/tests.tsx", Page_tests],
  ["./pages/home.tsx", Page_home],
  ["./pages/compte.tsx", Page_compte],
  ["./pages/compte.notifications.tsx", Page_compte_notifications],
  ["./pages/paiement-confirme.tsx", Page_paiementConfirme],
  ["./pages/tarifs.tsx", Page_tarifs],
  ["./pages/contact.tsx", Page_contact],
  ["./pages/exports.tsx", Page_exports],
  // New pages
  ["./pages/grille-matricielle-keno.tsx", Page_grille_matricielle_keno],
  ["./pages/reducteur.tsx", Page_reducteur],
  ["./pages/structures.tsx", Page_structures],
  ["./pages/relations.tsx", Page_relations],
  ["./pages/comparateur.tsx", Page_comparateur],
  ["./pages/multiplicateurs.tsx", Page_multiplicateurs],
  ["./pages/numeros-communs.tsx", Page_numeros_communs],
  ["./pages/calendrier.tsx", Page_calendrier],
  ["./pages/analyse-combinaison-keno.tsx", Page_analyse_combinaison_keno],
  ["./pages/taux-sortie-numeros-keno.tsx", Page_taux_sortie_numeros_keno],
  ["./pages/numeros-signature-keno.tsx", Page_numeros_signature_keno],
  ["./pages/numeros-plus-sortis-keno.tsx", Page_numeros_plus_sortis_keno],
  // Analyses pages
  ["./pages/admin.login.tsx", Page_admin_login],
  ["./pages/admin.activities.tsx", Page_admin_activities],
  // Espace Admin pages
  ["./pages/espace-admin.tsx", Page_espace_admin],
  ["./pages/espace-admin.users.tsx", Page_espace_admin_users],
  ["./pages/espace-admin.users.new.tsx", Page_espace_admin_users_new],
  ["./pages/espace-admin.users.$id.tsx", Page_espace_admin_users_id],
  ["./pages/espace-admin.users.view.$id.tsx", Page_espace_admin_users_view],
  ["./pages/espace-admin.tirages.tsx", Page_espace_admin_tirages],
  ["./pages/espace-admin.tirages.new.tsx", Page_espace_admin_tirages_new],
  ["./pages/espace-admin.tirages.$id.tsx", Page_espace_admin_tirages_id],
  ["./pages/espace-admin.licenses.tsx", Page_espace_admin_licenses],
  ["./pages/espace-admin.licenses.$id.tsx", Page_espace_admin_licenses_id],
  ["./pages/espace-admin.login-attempts.tsx", Page_espace_admin_login_attempts],
  ["./pages/espace-admin.notification-logs.tsx", Page_espace_admin_notification_logs],
  ["./pages/espace-admin.sondages.tsx", Page_espace_admin_sondages],
  ["./pages/espace-admin.announcements.tsx", Page_espace_admin_announcements],
  ["./pages/espace-admin.messages.tsx", Page_espace_admin_messages],
  ["./pages/espace-admin.logs.tsx", Page_espace_admin_logs],
  ["./pages/espace-admin.email-preview.tsx", Page_espace_admin_email_preview],
  ["./pages/espace-admin.conversion.tsx", Page_espace_admin_conversion],
  ["./pages/espace-admin.cohortes.tsx", Page_espace_admin_cohortes],
  ["./pages/espace-admin.promo-campaigns.tsx", Page_espace_admin_promo_campaigns],
  ["./pages/notifications.tsx", Page_notifications],
  ["./pages/labo.tsx", Page_labo],
  ["./pages/quartets.tsx", Page_quartets],
  ["./pages/mes-grilles.tsx", Page_mes_grilles],
  ["./pages/mon-keno.tsx", Page_mon_keno],
  ["./pages/analyses.tirage.$date.tsx", Page_analyses_tirage_date],
  ["./pages/tirage-du-jour.tsx", Page_tirage_du_jour],
  ["./pages/prochain-tirage.tsx", Page_prochain_tirage],
  ["./pages/pronostics-keno.tsx", Page_pronostics_keno],
  ["./pages/mon-avis.tsx", Page_mon_avis],
]);

// Loading fallback component
function PageLoader() {
  return (
    <div style={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      backgroundColor: 'var(--background, inherit)'
    }}>
      <Spinner size="lg" />
    </div>
  );
}

function makePageRoute(filename: string) {
  const Component = fileNameToComponent.get(filename);
  if (!Component) return null;

  // Check if it's a lazy component (has $$typeof for lazy)
  const isLazy = '$$typeof' in Component && Component.$$typeof === Symbol.for('react.lazy');

  if (isLazy) {
    return (
      <Suspense fallback={<PageLoader />}>
        <Component />
      </Suspense>
    );
  }

  // Static component - no Suspense needed
  return <Component />;
}

function toElement({
  trie,
  fileNameToRoute,
  makePageRoute,
}: {
  trie: LayoutTrie;
  fileNameToRoute: Map<string, string>;
  makePageRoute: (filename: string) => React.ReactNode;
}) {
  return [
    ...trie.topLevel.map((filename) => (
      <Route
        key={fileNameToRoute.get(filename)}
        path={fileNameToRoute.get(filename)}
        element={makePageRoute(filename)}
      />
    )),
    ...Array.from(trie.trie.entries()).map(([Component, child], index) => (
      <Route
        key={index}
        element={
          <Component>
            <Outlet />
          </Component>
        }
      >
        {toElement({ trie: child, fileNameToRoute, makePageRoute })}
      </Route>
    )),
  ];
}

type LayoutTrieNode = Map<
  React.ComponentType<{ children: React.ReactNode }>,
  LayoutTrie
>;
type LayoutTrie = { topLevel: string[]; trie: LayoutTrieNode };

function buildLayoutTrie(layouts: {
  [fileName: string]: React.ComponentType<{ children: React.ReactNode }>[];
}): LayoutTrie {
  const result: LayoutTrie = { topLevel: [], trie: new Map() };
  Object.entries(layouts).forEach(([fileName, components]) => {
    let cur: LayoutTrie = result;
    for (const component of components) {
      if (!cur.trie.has(component)) {
        cur.trie.set(component, {
          topLevel: [],
          trie: new Map(),
        });
      }
      cur = cur.trie.get(component)!;
    }
    cur.topLevel.push(fileName);
  });
  return result;
}

import { NotFound } from "./components/NotFound";
import { captureUtmFromUrl, rememberVisit } from "./helpers/attribution";
import { trackSignUp } from "./helpers/analytics";
import { findNavItem } from "./helpers/navLookup";

import { useLocation, useNavigationType } from "react-router-dom";

export default function ScrollManager() {
  const { pathname, search, hash } = useLocation();
  const navType = useNavigationType();

  useEffect(() => {
    if (navType === "POP") return;
    if (hash) return;
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, search, hash, navType]);

  // À chaque navigation interne, on regarde si l'URL porte un utm_source
  // et on le persiste en localStorage (last-touch attribution sur 7j).
  useEffect(() => {
    captureUtmFromUrl();
    rememberVisit();

    // Marqueurs de retour OAuth Google (spec 18) posés par le callback serveur :
    // `auth=google` (toujours, → flag has_session posé dans index.tsx) et `new=1`
    // (création → track signup). On les consomme puis on nettoie l'URL (évite le
    // double-comptage au refresh et le `?auth=google` disgracieux dans la barre).
    const params = new URLSearchParams(window.location.search);
    const hadAuthMarker = params.has("auth") || params.has("new");
    if (params.get("new") === "1") {
      // Le callback Google renvoie sur le ?next= signé : l'adresse d'arrivée est l'outil d'origine.
      trackSignUp("google", findNavItem(window.location.pathname)?.path ?? null);
    }
    if (hadAuthMarker) {
      params.delete("auth");
      params.delete("new");
      const qs = params.toString();
      window.history.replaceState(null, "", window.location.pathname + (qs ? `?${qs}` : ""));
    }
  }, [search]);

  return null;
}

export function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <ScrollManager />
        <GlobalContextProviders>
          <PromoCampaignBanner />
          <Routes>
          {toElement({
            trie: buildLayoutTrie({
              "./pages/login.tsx": PageLayout_login,
              "./pages/inscription.tsx": PageLayout_inscription,
              "./pages/verify-email.tsx": PageLayout_verifyEmail,
              "./pages/mot-de-passe-oublie.tsx": PageLayout_motDePasseOublie,
              "./pages/reinitialiser-mot-de-passe.tsx": PageLayout_reinitialiserMotDePasse,
              "./pages/tests.tsx": PageLayout_tests,
              "./pages/home.tsx": PageLayout_home,
              "./pages/compte.tsx": PageLayout_compte,
              "./pages/compte.notifications.tsx": PageLayout_compte,
              "./pages/paiement-confirme.tsx": PageLayout_paiementConfirme,
              "./pages/tarifs.tsx": PageLayout_tarifs,
              "./pages/contact.tsx": PageLayout_contact,
              "./pages/exports.tsx": PageLayout_exports,
              // New pages
              "./pages/grille-matricielle-keno.tsx": PageLayout_grille_matricielle_keno,
              "./pages/reducteur.tsx": PageLayout_reducteur,
              "./pages/structures.tsx": PageLayout_structures,
              "./pages/relations.tsx": PageLayout_relations,
              "./pages/comparateur.tsx": PageLayout_comparateur,
              "./pages/multiplicateurs.tsx": PageLayout_multiplicateurs,
              "./pages/numeros-communs.tsx": PageLayout_numeros_communs,
              "./pages/calendrier.tsx": PageLayout_calendrier,
              "./pages/analyse-combinaison-keno.tsx": PageLayout_analyse_combinaison_keno,
              "./pages/taux-sortie-numeros-keno.tsx": PageLayout_taux_sortie_numeros_keno,
              "./pages/numeros-signature-keno.tsx": PageLayout_numeros_signature_keno,
              "./pages/numeros-plus-sortis-keno.tsx": PageLayout_numeros_plus_sortis_keno,
              // Analyses pages
              "./pages/admin.login.tsx": PageLayout_admin_login,
              "./pages/admin.activities.tsx": PageLayout_admin_activities,
              // Espace Admin pages
              "./pages/espace-admin.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.users.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.users.new.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.users.$id.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.users.view.$id.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.tirages.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.tirages.new.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.tirages.$id.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.licenses.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.licenses.$id.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.login-attempts.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.notification-logs.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.sondages.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.announcements.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.messages.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.logs.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.email-preview.tsx": PageLayout_espace_admin,
              "./pages/espace-admin.conversion.tsx": PageLayout_espace_admin_conversion,
              "./pages/espace-admin.cohortes.tsx": PageLayout_espace_admin_cohortes,
              "./pages/espace-admin.promo-campaigns.tsx": PageLayout_espace_admin,
              "./pages/notifications.tsx": PageLayout_notifications,
              "./pages/labo.tsx": PageLayout_labo,
              "./pages/quartets.tsx": PageLayout_quartets,
              "./pages/mes-grilles.tsx": PageLayout_mes_grilles,
              "./pages/mon-keno.tsx": PageLayout_mon_keno,
              "./pages/analyses.tirage.$date.tsx": PageLayout_analyses_tirage_date,
              "./pages/tirage-du-jour.tsx": PageLayout_tirage_du_jour,
              "./pages/prochain-tirage.tsx": PageLayout_prochain_tirage,
              "./pages/pronostics-keno.tsx": PageLayout_pronostics_keno,
              "./pages/mon-avis.tsx": PageLayout_mon_avis,
            }),
            fileNameToRoute,
            makePageRoute,
          })}
          <Route path="*" element={<NotFound />} />
          </Routes>
        </GlobalContextProviders>
      </BrowserRouter>
    </ErrorBoundary>
  );
}
